<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { PrimaryButton, SecondaryButton, DialogConfirm, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunityProviderStore } from '@/stores/communityProviderStore';
import { useCommunityCompaniesStore } from '@/stores/communityCompaniesStore';
import ServiceFeesTab from '@/components/service-fees/ServiceFeesTab.vue';
import TariffServiceFeeMatrix from '@/components/service-fees/TariffServiceFeeMatrix.vue';
import { matrixRowsForProvider } from '@/utils/tariffServiceFeeMatrixUtils.js';

const $log = inject('log');
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const snackbarStore = useSnackbarStore();
const providerStore = useCommunityProviderStore();
const companiesStore = useCommunityCompaniesStore();

const providerId = computed(() => route.params.id || null);
const isNew = computed(() => !providerId.value);

const tab = ref('masterData');
const deleteDialog = ref(false);

function makeEmptyForm() {
  return {
    name: '',
    nameFriendly: '',
    legal: '',
    accountingReference: '00',
    notes: '',
    serviceFees: [],
  };
}

const form = ref(makeEmptyForm());

async function loadData() {
  if (!providerStore.allProviders.length)
    await providerStore.fetchProviders().catch(() => {});
  if (!companiesStore.allCommunityCompanies.length)
    await companiesStore.fetchCommunityCompanies().catch(() => {});

  if (providerId.value) {
    const provider = providerStore.providerById.get(providerId.value);
    if (provider)
      form.value = JSON.parse(JSON.stringify(provider));
  } else {
    // a route switch from a provider detail to the create route reuses the
    // component, the stale form must not leak into the new provider
    form.value = makeEmptyForm();
  }
}
loadData();
watch(providerId, loadData);

const matrixRows = computed(() => matrixRowsForProvider(form.value, assignedCompanies.value));

const assignedCompanies = computed(() => companiesStore.allCommunityCompanies.filter(c => {
  const refId = c.communityProvider && typeof c.communityProvider === 'object'
    ? c.communityProvider._id
    : c.communityProvider;
  return providerId.value && refId === providerId.value;
}));

const assignedHeader = computed(() => [
  { title: 'ECN', value: 'ecn' },
  { title: t('name'), value: 'name' },
  { title: t('communityProviders.memberCount'), value: 'memberCount' },
]);

async function onSave() {
  if (!form.value.name || !form.value.nameFriendly) {
    snackbarStore.showSnackbar(t('communityProviders.nameRequired'), '', 'Error', 8000, []);
    return;
  }
  try {
    if (isNew.value) {
      const created = await providerStore.createProvider(form.value);
      snackbarStore.showSnackbar(t('saved'), '', 'Success', 4000, []);
      router.replace({ name: 'communityProviderForm', params: { id: created._id } });
    } else {
      await providerStore.updateProvider(providerId.value, form.value);
      form.value = JSON.parse(JSON.stringify(providerStore.providerById.get(providerId.value)));
      snackbarStore.showSnackbar(t('saved'), '', 'Success', 4000, []);
    }
  } catch (error) {
    $log?.warn('save communityProvider failed', error);
    if (error?.response?.status === 409)
      snackbarStore.showSnackbar(t('communityProviders.versionConflict'), '', 'Error', 10000, []);
    else
      snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}

function onServiceFeesUpdate(fees) {
  form.value.serviceFees = fees;
  if (!isNew.value)
    onSave();
}

async function onDeleteConfirmed() {
  deleteDialog.value = false;
  try {
    await providerStore.deleteProvider(providerId.value);
    snackbarStore.showSnackbar(t('deleted'), '', 'Success', 4000, []);
    router.push({ name: 'communityProviders' });
  } catch (error) {
    $log?.warn('delete communityProvider failed', error);
    if (error?.response?.status === 409)
      snackbarStore.showSnackbar(t('communityProviders.deleteStillAssigned'), '', 'Error', 10000, []);
    else
      snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}
</script>

<template>
  <v-container>
    <div class="d-flex justify-space-between align-center mb-4">
      <p class="textHeadline">
        {{ isNew ? t('communityProviders.create') : form.nameFriendly || t('communityProviders.title') }}
      </p>
      <div>
        <SecondaryButton
          v-if="!isNew"
          class="mr-2"
          :text="t('delete')"
          @click="deleteDialog = true"
        />
        <PrimaryButton :text="t('save')" @click="onSave" />
      </div>
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab value="masterData">{{ t('communityProviders.tabMasterData') }}</v-tab>
      <v-tab value="companies" :disabled="isNew">{{ t('communityProviders.tabCompanies') }}</v-tab>
      <v-tab value="serviceFees" :disabled="isNew">{{ t('serviceFees.title') }}</v-tab>
      <v-tab value="matrix" :disabled="isNew">{{ t('matrix.title') }}</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="masterData" class="pt-2">
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.nameFriendly" :label="t('communityProviders.nameFriendly')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.name" :label="t('name')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.legal" :label="t('communityProviders.legal')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.accountingReference" :label="t('communityProviders.accountingReference')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-textarea v-model="form.notes" :label="t('communityProviders.notes')" bg-color="white" variant="outlined" density="compact" rows="3" />
      </v-window-item>

      <v-window-item value="companies">
        <v-data-table
          class="elevation-2 mainViewContent"
          :headers="assignedHeader"
          :items="assignedCompanies"
          :items-per-page="25"
          density="comfortable"
        />
        <p class="text-body-2 mt-2">{{ t('communityProviders.assignHint') }}</p>
      </v-window-item>

      <v-window-item value="serviceFees">
        <ServiceFeesTab
          :service-fees="form.serviceFees || []"
          @update:service-fees="onServiceFeesUpdate"
        />
      </v-window-item>

      <v-window-item value="matrix">
        <TariffServiceFeeMatrix :rows="matrixRows" />
      </v-window-item>
    </v-window>

    <DialogConfirm
      v-model="deleteDialog"
      :title="t('communityProviders.deleteTitle')"
      :message="t('communityProviders.deleteConfirm')"
      :confirmText="t('confirm')"
      :cancelText="t('cancel')"
      @confirm="onDeleteConfirmed"
      @cancel="deleteDialog = false"
    />
  </v-container>
</template>
